import type { FC } from 'react';
import { useIntl } from 'react-intl';

import type { SelectOption } from '@/components/Select';
import { Select } from '@/components/Select';
import { Visibility } from '@/components/Visibility';
import { commonMessages } from '@/messages/common';
import { theme } from '@/theme/getTheme';

import type { NewsPageType } from '../../types';

import * as S from './TagsFilter.styles';

interface TagsFilterProps {
  newsPagesCollection: Array<NewsPageType | null>;
  onTagClick: (tag: string) => void;
  selectedTag: string;
}

const TagsFilter: FC<TagsFilterProps> = ({
  newsPagesCollection,
  onTagClick,
  selectedTag,
}) => {
  const tagsSet = new Set<string>();
  const { formatMessage } = useIntl();

  tagsSet.add(formatMessage(commonMessages.newsTags));

  newsPagesCollection.forEach((item) => {
    item?.tagsCollection?.items.forEach((tag) => {
      if (tag?.value) {
        tagsSet.add(tag.value);
      }
    });
  });

  const tagsArray = Array.from(tagsSet);
  const options: SelectOption[] = tagsArray.map((item) => ({
    label: item,
    value: item,
  }));

  return (
    <>
      <Visibility mdVisible={true}>
        <S.TagsContainer>
          {tagsArray.map((tag) => (
            <S.Tag
              key={tag}
              $selected={selectedTag === tag}
              onClick={() => onTagClick(tag)}
            >
              {tag}
            </S.Tag>
          ))}
        </S.TagsContainer>
      </Visibility>
      <Visibility mdHidden={true}>
        <S.SelectContainer>
          <Select
            colorVariant='grey'
            isSearchable={false}
            onChange={(option) => onTagClick(option?.value ?? '')}
            options={options}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderBottom: '1px solid rgba(99, 99, 99, 0.3)',
                padding: theme.spacing(1.5, 1),
              }),
              singleValue: (styles) => ({
                ...styles,
                color: theme.colors.primaryDark,
                textTransform: 'uppercase',
                fontWeight: theme.fontWeights.medium,
                fontFamily: theme.fontFamilies.italianPlate,
                fontSize: theme.fontSizes.p1,
              }),
              option: (styles) => ({
                ...styles,
                color: theme.colors.primaryDark,
                textTransform: 'uppercase',
                fontWeight: theme.fontWeights.medium,
                fontFamily: theme.fontFamilies.italianPlate,
                fontSize: theme.fontSizes.p1,
              }),
              dropdownIndicator: (baseStyles) => ({
                ...baseStyles,
                color: theme.colors.primaryDark,
              }),
            }}
            value={{
              label: selectedTag,
              value: selectedTag,
            }}
          />
        </S.SelectContainer>
      </Visibility>
    </>
  );
};

export default TagsFilter;
